/**
 * What an uploaded image is called inside a comment body.
 *
 * The bucket is private, so the only way to show a file is a pre-signed URL —
 * and a pre-signed URL is good for an hour. Writing one into the body would
 * produce a comment whose images are broken by lunchtime and permanently
 * afterwards. So `attachment:<id>` is what is written, everywhere the markdown
 * is the thing being stored, and the URL is resolved only at the moment
 * something needs to paint it. Two places do that, both through
 * `resolveAttachmentHref`:
 *
 *   - the editor, as Crepe's `proxyDomURL`, which maps a node's stored `src`
 *     to what its `<img>` loads;
 *   - the display, as `<Markdown resolveHref>`.
 *
 * An earlier version rewrote the markdown string on the way in and out
 * instead. It could not work: remark escapes the `&` in a query string when it
 * serialises, so the URL coming back out never matched the one that went in,
 * and the comment silently stored a URL that would expire. Resolving at paint
 * time has nothing to match.
 *
 * Kept here, free of React and of the DOM, so it is testable on its own.
 */

/** An attachment as the API lists it. Only the two fields this module needs. */
export type AttachmentRef = {
  id: string
  url: string | null
}

const SCHEME = "attachment:"

/** The `attachment:<id>` form for an id. */
export function attachmentRef(id: string): string {
  return `${SCHEME}${id}`
}

/** The id inside an `attachment:<id>`, or null for anything else. Prose that
 *  merely contains the word ("attachment: see below") yields an id nothing
 *  matches, which resolves to null a line later. */
export function refId(href: string): string | null {
  return href.startsWith(SCHEME) ? href.slice(SCHEME.length) : null
}

/** For `<Markdown resolveHref>`: an `attachment:` ref becomes a signed URL,
 *  and everything else is left for the renderer to judge. */
export function resolveAttachmentHref(attachments: readonly AttachmentRef[]) {
  const url = new Map(attachments.map((a) => [a.id, a.url]))
  return (href: string): string | null => {
    const id = refId(href)
    return id === null ? null : (url.get(id) ?? null)
  }
}

/** The attachments a body does not already show inline — what the chip list
 *  under the body renders. A file painted in the markdown does not also need
 *  a card below it. */
export function attachmentsNotInBody<T extends { id: string }>(
  body: string,
  attachments: readonly T[],
): T[] {
  return attachments.filter((a) => !body.includes(`${SCHEME}${a.id}`))
}
